<template>
  <div>
    <div
      v-if="qualification.verifiedAt && qualification.administrator"
      style="position: absolute; right: 1rem; margin-top: -2.4rem"
    >
      <v-tooltip bottom>
        <img slot="activator" src="/img/verified.png" width="30" />
        <span>
          Verified by {{ qualification.administrator.firstName }}
          {{ qualification.administrator.lastName }} on
          {{ new Date(qualification.verifiedAt) | date("d MMMM yyy") }}
        </span>
      </v-tooltip>
    </div>
    <div v-else style="position: absolute; right: 1rem; margin-top: -3rem">
      <Flag
        v-for="(f, i) in flags"
        :key="`qualification-flag-${i}`"
        :value="f"
        icon
      />
      <CreateFlag
        :target="{ ...qualification, type: 'ProfileQualification' }"
        :profile="profile"
        :filter="['grade_value', 'qualification_dates']"
        @done="$emit('refresh')"
      >
        <v-btn icon flat :color="themes.client.light.cancel" slot="activator">
          <v-icon>mdi-flag</v-icon>
        </v-btn>
      </CreateFlag>
    </div>

    <v-card flat>
      <v-layout row wrap align-center fill-height>
        <v-flex xs12 order-xs2 order-md1 v-if="sortedMajors.length > 0">
          <div class="text-xs-center py-3 mt-2">
            <div class="title">
              <span
                v-if="
                  qualification.qualificationType &&
                  qualification.qualificationType.acronym
                "
              >
                {{ qualification.qualificationType.acronym }}
              </span>
              <span v-else> {{ qualification.qualificationTypeName }} </span>
              <span v-if="qualification.specialization">
                in {{ qualification.specialization }}
              </span>
            </div>
            <div class="subheading" v-if="qualification.institutionName">
              at {{ qualification.institutionName }}
            </div>
            <span
              v-if="requiresProofOfRegistration"
              class="py-2"
              key="missing-proof"
            >
              <v-icon left :class="themes.client.light.warningText"
                >mdi-alert-circle</v-icon
              >
              <span :class="themes.client.light.warningText"
                >Proof of Registration Required</span
              >
            </span>
            <span
              v-if="requiresQualificationTranscript"
              class="py-2"
              key="missing-transcript"
            >
              <v-icon left :color="themes.client.light.warning"
                >mdi-alert-circle</v-icon
              >
              <span :class="themes.client.light.warningText"
                >Transcript Required</span
              >
            </span>
          </div>
        </v-flex>
        <v-flex
          xs12
          md3
          order-xs1
          order-md2
          class="text-xs-center pt-3 mt-2"
          :class="themes.client.light[dateColor]"
        >
          <CompletionIndicator
            :start="qualification.startsAt"
            :end="qualification.endsAt"
            :avatar="logo"
            :size="120"
          />
          <div
            v-if="qualification.startsAt || qualification.endsAt"
            class="pt-3"
          >
            <span
              v-if="
                qualification.startsAt &&
                qualification.startsAt !== qualification.endsAt
              "
            >
              {{ new Date(qualification.startsAt) | date("MMM yyy") }} -
            </span>
            <span v-if="qualification.endsAt">
              {{ new Date(qualification.endsAt) | date("MMM yyy") }}
            </span>
          </div>
          <div v-if="qualification.accreditedAt" class="caption">
            <v-tooltip top :color="themes.client.light.tooltip">
              <span slot="activator">
                <v-icon small :class="themes.client.light[dateColor]"
                  >mdi-certificate</v-icon
                >
                {{ new Date(qualification.accreditedAt) | date("d MMMM yyy") }}
              </span>
              <span>Accreditation Date</span>
            </v-tooltip>
          </div>
        </v-flex>
        <v-flex
          xs12
          md6
          order-xs3
          v-if="sortedMajors.length > 0"
          key="qualification-majors"
        >
          <v-flex justify-center>
            <v-list
              subheader
              class="transparent"
              style="justify-content: center"
            >
              <div v-for="(component, i) in sortedMajors" :key="i">
                <v-subheader
                  v-if="i < 1"
                  :style="
                    $vuetify.breakpoint.smAndDown
                      ? 'justify-content: center;'
                      : ''
                  "
                >
                  {{ isSecondary ? "Subjects" : "Majors" }}
                </v-subheader>
                <v-list-tile>
                  <v-list-tile-avatar>
                    <v-avatar size="30" color="white">
                      <v-icon :color="themes.client.light.muted"
                        >mdi-check</v-icon
                      >
                    </v-avatar>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title>{{
                      component.description
                    }}</v-list-tile-title>
                    <v-list-tile-sub-title v-if="component.gradeValue">
                      {{ component.gradeValue }} {{ component.gradeScale }}
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
              </div>
            </v-list>
          </v-flex>
        </v-flex>
        <v-flex xs12 md6 order-xs3 v-else key="qualification-description">
          <div class="text-xs-center py-3 mt-2">
            <div class="title">
              <span
                v-if="
                  qualification.qualificationType &&
                  qualification.qualificationType.acronym
                "
              >
                {{ qualification.qualificationType.acronym }}
              </span>
              <span v-else> {{ qualification.qualificationTypeName }} </span>
              <span v-if="qualification.specialization">
                in {{ qualification.specialization }}
              </span>
            </div>
            <div class="subheading" v-if="qualification.institutionName">
              at {{ qualification.institutionName }}
            </div>
            <span
              v-if="requiresProofOfRegistration"
              class="py-2"
              key="missing-proof"
            >
              <v-icon left :class="themes.client.light.warningText"
                >mdi-alert-circle</v-icon
              >
              <span :class="themes.client.light.warningText"
                >Proof of Registration Required</span
              >
            </span>
            <span
              v-if="requiresQualificationTranscript"
              class="py-2"
              key="missing-transcript"
            >
              <v-icon left :color="themes.client.light.warning"
                >mdi-alert-circle</v-icon
              >
              <span :class="themes.client.light.warningText"
                >Transcript Required</span
              >
            </span>
          </div>
        </v-flex>
        <v-flex
          xs12
          md3
          order-xs4
          v-if="qualification.gradeNormalized"
          class="pt-3 mt-2"
        >
          <div class="text-xs-center">
            <v-progress-circular
              :value="Number(qualification.gradeNormalized)"
              :color="themes.client.light[gradeColor]"
              :size="120"
              :width="4"
              :rotate="-90"
            >
              <v-avatar size="110">
                <span
                  class="display-1"
                  :class="themes.client.light[`${gradeColor}Text`]"
                >
                  {{ qualification.gradeValue }}
                </span>
              </v-avatar>
            </v-progress-circular>
            <div
              v-if="qualification.gradeScale"
              :class="themes.client.light[`${gradeColor}Text`]"
              class="pt-3"
            >
              {{ qualification.gradeScale }}
            </div>
          </div>
        </v-flex>
      </v-layout>
    </v-card>
  </div>
</template>

<script>
// Components
import CompletionIndicator from "@/components/client/candidate/profile/qualifications/CompletionIndicator";
import CreateFlag from "@/components/client/flags/CreateFlag";
import Flag from "@/components/client/flags/Flag";

// Helpers
import { byGradeValue } from "@/sorting/QualificationComponents";

export default {
  components: { CompletionIndicator, CreateFlag, Flag },

  props: {
    profile: { type: Object, required: true },
    qualification: { type: Object, required: true },
    flags: { type: Array, required: true },
    requiresProofOfRegistration: { type: Boolean, default: () => false },
    requiresQualificationTranscript: { type: Boolean, default: () => false },
  },

  computed: {
    dateColor() {
      const datesFlagged = this.flags.some(
        (f) => f.key.indexOf("qualification_dates") >= 0
      );
      return datesFlagged ? "warningText" : "mutedText";
    },
    gradeColor() {
      const gradeFalgged = this.flags.some(
        (f) => f.key.indexOf("grade_value") >= 0
      );
      return gradeFalgged ? "error" : "primary";
    },
    isSecondary() {
      return (
        _.get(
          this.qualification,
          "qualificationType.qualificationCategoryName"
        ) === "Secondary Education"
      );
    },
    logo() {
      return _.get(this.qualification, "institution.logo");
    },
    sortedMajors() {
      if (!_.get(this.qualification, "qualificationComponents")) return [];

      const components = JSON.parse(
        JSON.stringify(this.qualification.qualificationComponents)
      );
      return components
        .filter((c) => this.isSecondary || c.disciplines[0])
        .sort(byGradeValue);
    },
  },
};
</script>
